import React, { useEffect, useState } from 'react';
import { workEntries, gsocLink, instagramLink, twitterLink, acquisitionLink } from './profile';
import './Portfolio.css';
import { trackEvent } from './analytics';

const Arrow = () => (
  <svg className="arrow" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M4 12 12 4M4 4h8v8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Expand = () => (
  <svg className="expand" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M4 8h8M8 4v8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

function goToChapter(event, id) {
  event.preventDefault();
  const chapter = document.getElementById(id);
  if (!chapter) return;
  trackEvent('section_navigation', { section_id: id, link_location: 'introduction' });
  if (chapter.tagName === 'DETAILS') chapter.open = true;
  chapter.scrollIntoView({
    behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    block: 'start',
  });
  window.history.replaceState(null, '', `#${id}`);
}

function WorkSummary({ entry }) {
  return <>
    <div className="entry-heading">
      <h3>{entry.name}</h3>
      <span className="entry-year">{entry.year}</span>
      {entry.current && <span className="current-marker" aria-label="Current role" />}
    </div>
    <p className="entry-role">{entry.role}{entry.status && <> · <a href={entry.statusLink} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('reference_click', { link_name: 'rilo_acquisition', link_location: 'work' })}>{entry.status} <Arrow /></a></>}</p>
    <p className="entry-description">{entry.description}</p>
  </>;
}

function WorkEntry({ entry }) {
  return <article className="work-entry" id={entry.id}>
    <WorkSummary entry={entry} />
    {entry.exploration && <details className="context-disclosure" onToggle={event => {
      if (event.currentTarget.open) trackEvent('project_expand', { project_id: entry.id });
    }}>
      <summary>Other ideas I explored <Expand /></summary>
      <p>{entry.exploration}</p>
    </details>}
  </article>;
}

function Earlier() {
  return <section className="profile-section" id="earlier" aria-labelledby="earlier-title">
    <h2 id="earlier-title">University years</h2>
    <div className="college-chapter">
      <div className="education">
        <div><h3>IIT (BHU), Varanasi</h3><span>2019–23</span></div>
      </div>
      <article className="earlier-entry" id="exawizards">
        <div><h4>ExaWizards · AI Engineer</h4><span>2022</span></div>
        <p>Summer internship spanning computer vision on iOS and cloud infrastructure for deploying AI products.</p>
      </article>
      <article className="earlier-entry" id="gsoc">
        <div><h4>Google Summer of Code · Oppia</h4><span>2021</span></div>
        <p>Improved Oppia’s lesson editor so creators could collaborate and keep working through connection loss without losing their edits.</p>
      </article>
      <article className="earlier-entry" id="early-teams">
        <h4>Early product teams</h4>
        <p>Engineering contributions at KyuKey, Kohbee, and Acadza.</p>
      </article>
      <article className="earlier-entry" id="campus">
        <h4>On campus</h4>
        <p>Students’ Parliament; COPS (Club of Programmers); the Training & Placement Cell; and Technex, IIT (BHU)’s technical festival.</p>
      </article>
    </div>
  </section>;
}

const sections = [
  ['about', 'About', 'About'],
  ['work', 'Work & ventures', 'Work'],
  ['earlier', 'University years', 'University'],
  ['writing', 'Writing', 'Writing'],
  ['elsewhere', 'Outside work', 'Outside work'],
];

function SocialLinks({ className }) {
  const linkLocation = className === 'sidebar-socials' ? 'sidebar' : 'introduction';
  const trackSocial = network => trackEvent('social_click', { social_network: network, link_location: linkLocation });
  return <div className={`social-links ${className}`} role="group" aria-label="Contact and social profiles">
    <a href="mailto:guptaaryaman22@gmail.com" onClick={() => trackEvent('contact_click', { contact_method: 'email', link_location: linkLocation })}>Email <Arrow /></a>
    <a href="https://www.linkedin.com/in/ryaman/" target="_blank" rel="noopener noreferrer" onClick={() => trackSocial('linkedin')}>LinkedIn <Arrow /></a>
    <a href="https://github.com/atpug22" target="_blank" rel="noopener noreferrer" onClick={() => trackSocial('github')}>GitHub <Arrow /></a>
    <a href={twitterLink} target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)" onClick={() => trackSocial('x')}>X <Arrow /></a>
    <a href={instagramLink} target="_blank" rel="noopener noreferrer" onClick={() => trackSocial('instagram')}>Instagram <Arrow /></a>
  </div>;
}

export default function Portfolio() {
  const [active, setActive] = useState('about');
  useEffect(() => {
    const viewedSections = new Set();
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        setActive(entry.target.id);
        if (!viewedSections.has(entry.target.id)) {
          viewedSections.add(entry.target.id);
          trackEvent('section_view', { section_id: entry.target.id });
        }
      });
    }, { rootMargin: '-12% 0px -65% 0px', threshold: 0 });
    document.querySelectorAll('main > section[id]').forEach(section => observer.observe(section));
    const target = document.getElementById(window.location.hash.slice(1));
    if (target && target.tagName === 'DETAILS') target.open = true;
    return () => observer.disconnect();
  }, []);

  return <div className="profile-layout" id="top">
    <aside className="identity">
      <div className="identity-inner">
        <h1><a className="identity-name" href="#about">Aryaman Gupta<span>.</span></a></h1>
        <p className="identity-description">Tech, startups<br /> & the world outside</p>
        <p className="identity-location">Bengaluru, India</p>
        <nav className="section-nav" aria-label="Page sections">
          {sections.map(([id, label, shortLabel]) => <a
            key={id}
            href={`#${id}`}
            className={active === id ? 'active' : ''}
            aria-current={active === id ? 'location' : undefined}
            onClick={() => trackEvent('section_navigation', { section_id: id, link_location: 'navigation' })}
          ><span className="nav-marker" /><span className="nav-label-full">{label}</span><span className="nav-label-short">{shortLabel}</span></a>)}
        </nav>
        <SocialLinks className="sidebar-socials" />
      </div>
    </aside>
    <main className="profile-content">
      <section className="profile-intro" id="about" aria-label="About Aryaman">
        <p className="intro-lead">I work on AI & ML at <a href="#adobe" onClick={event => goToChapter(event, 'adobe')}>Adobe</a>. Previously on the founding team at <a href="#rilo" onClick={event => goToChapter(event, 'rilo')}>Rilo</a>, <a href={acquisitionLink} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('reference_click', { link_name: 'rilo_acquisition', link_location: 'introduction' })}>acquired by Adobe</a> in 2026.</p>
        <p>I’m into technology and startups: building products, exploring ideas, and following what other people are working on.</p>
        <p className="intro-personal"><a href="#earlier" onClick={event => goToChapter(event, 'earlier')}>IIT (BHU)</a> alumnus. Google Summer of Code contributor to <a href="#gsoc" onClick={event => goToChapter(event, 'gsoc')}>Oppia</a>.</p>
        <SocialLinks className="mobile-socials" />
      </section>

      <section className="profile-section" id="work" aria-labelledby="work-title">
        <h2 id="work-title">Work & ventures</h2>
        <div className="work-entries">{workEntries.map(entry => <WorkEntry key={entry.id} entry={entry} />)}</div>
      </section>
      <Earlier />

      <section className="profile-section writing-section" id="writing" aria-labelledby="writing-title">
        <h2 id="writing-title">Writing</h2>
        <a className="writing-link" href={gsocLink} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('writing_click', { article_id: 'gsoc_oppia' })}>
          <span>Collaborative and offline editing at Oppia</span><span className="writing-year">2021 <Arrow /></span>
        </a>
        <p className="writing-description">A write-up of my Google Summer of Code project.</p>
      </section>

      <section className="profile-section elsewhere-section" id="elsewhere" aria-labelledby="elsewhere-title">
        <h2 id="elsewhere-title">Outside work</h2>
        <p>I read, follow the news, travel, and am learning to DJ. Chronically online.</p>
      </section>
      <footer className="profile-footer"><span>Aryaman Gupta</span><a href="#top">Back to top ↑</a></footer>
    </main>
  </div>;
}
